import SEO from "../../../components/seo.jsx"
import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import HeroImg from "../../../assets/maleHt-consultation.png";
import BreadcrumbImg from "../../../assets/breadcrumb-bg.jpg";
import OverviewImg from "../../../assets/maleHair.webp";
import TechniquesImg from "../../../assets/maleHair-tech.jpg";
import BenefitsImg from "../../../assets/mHt-benefits.jpg";

gsap.registerPlugin(ScrollTrigger);
const cinzel = { fontFamily: "'Cinzel', serif" };

const PAGE = {

  hero: {
    eyebrow: "For Men",
    heading: "Male Hair Transplant in Pune",
    sub: "Natural, lasting restoration for male pattern baldness performed by experienced surgeons at Clinique Internationale.",
  },

  breadcrumb: [
    { label: "Home", href: "/" },
    { label: "Male Hair Transplant", href: null },
  ],

  intro: {
    eyebrow: "Overview",
    heading: "Male Hair Transplant in Pune",
    paragraphs: [
      "Male pattern baldness is a common concern for many men, and the desire to restore a full head of hair often leads them to explore various hair restoration options. In recent years, Pune has emerged as a leading destination for male hair transplants.",
    ],
    link: { label: "Hair Transplant in Pune", href: "/hair-transplant-in-pune.php" },
    afterLink:
      ", exploring what it is, the benefits it offers, the causes of hair loss, and the hair transplant surgery options available at Clinique Internationale.",
  },

  whatIsIt: {
    eyebrow: "The Procedure",
    heading: "What Is Male Hair Transplant in Pune?",
    paragraphs: [
      "Male hair transplant in Pune is a specialised medical procedure designed to address male pattern baldness or hair loss. It involves the surgical removal of hair follicles from one part of the body, typically the back or sides of the head, and their transplantation to areas where hair is thinning or receding. This technique is highly effective and provides a natural-looking result.",
    ],
  },

  causes: {
    eyebrow: "Understanding Hair Loss",
    heading: "Causes of Hair Loss",
    intro:
      "Before delving into the details of hair transplant surgery, it's essential to understand the primary causes of hair loss in men. Male pattern baldness, also known as androgenetic alopecia, is the most common cause. This hereditary condition leads to hair thinning and receding hairlines. Other contributing factors include:",
    items: [
      { title: "Stress", desc: "High stress levels can lead to hair shedding and thinning." },
      { title: "Nutritional Deficiencies", desc: "A lack of essential nutrients can weaken hair and lead to loss." },
      { title: "Medical Conditions", desc: "Conditions such as alopecia areata can cause hair loss." },
      { title: "Hormonal Imbalances", desc: "Hormonal changes, such as those during puberty or from medical conditions, can contribute to hair loss." },
      { title: "Medications", desc: "Some medications, like chemotherapy drugs, can cause hair loss as a side effect." },
    ],
  },

  techniques: {
    eyebrow: "At Clinique Internationale",
    heading: "Hair Transplant Surgery Techniques",
    intro:
      "Clinique Internationale is a renowned destination for male hair transplant in Pune, offering a range of surgical techniques to meet individual needs, backed by state-of-the-art facilities and experienced surgeons.",
    items: [
      {
        num: "01",
        title: "Follicular Unit Transplantation (FUT)",
        desc: "Involves the removal of a strip of skin containing hair follicles from the donor area, which is then divided into individual grafts. These grafts are meticulously transplanted into the recipient area, ensuring a natural appearance.",
      },
      {
        num: "02",
        title: "Follicular Unit Extraction (FUE)",
        desc: "A minimally invasive procedure that involves extracting individual hair follicles from the donor area using a specialised tool. These follicles are then transplanted into the areas with hair loss, leaving tiny, virtually unnoticeable scars.",
      },
      {
        num: "03",
        title: "Direct Hair Implantation (DHI)",
        desc: "A cutting-edge technique offering precise and quick hair transplantation. It uses a Choi Implanter Pen to implant hair directly without prior incisions, resulting in minimal discomfort and downtime.",
      },
      {
        num: "04",
        title: "Platelet-Rich Plasma (PRP) Therapy",
        desc: "Often used alongside hair transplant surgery to enhance the growth and thickness of transplanted hair, using the patient's own platelet-rich plasma to stimulate hair follicles.",
      },
    ],
  },

  benefits: {
    eyebrow: "Why Choose Pune",
    heading: "Benefits of Male Hair Transplant in Pune",
    intro: "Male hair transplants in Pune offer numerous advantages, making them a popular choice for those seeking hair restoration.",
    items: [
      { title: "Natural-Looking Results", desc: "The procedure ensures transplanted hair seamlessly blends with existing hair, creating a realistic and undetectable outcome." },
      { title: "Cost Effective", desc: "Compared to many Western countries, the cost of hair transplant surgery in Pune is significantly lower." },
      { title: "High-Quality Clinics", desc: "Pune boasts world-class medical facilities and highly trained surgeons who specialise in hair transplant procedures." },
      { title: "Minimal Downtime", desc: "Patients can usually resume daily activities within a few days, with a relatively quick and painless recovery." },
      { title: "Improved Confidence", desc: "Regaining a full head of hair can significantly boost confidence and self-esteem." },
    ],
  },

  cta: {
    eyebrow: "Take the First Step",
    heading: "Begin Your Hair Restoration Journey",
    sub: "Book a personalised consultation with our team at Clinique Internationale and take the first step toward natural, lasting results.",
    primary: { label: "Book Consultation", href: "https://api.whatsapp.com/send?phone=919082123009&text=I%27m%20interested%20in%20Hair%20Restoration" },
    secondary: { label: "View All Treatments", href: "/about-hair-transplants.php" },
  },
};

/* ============================================================
   REUSABLE SUB-COMPONENTS
============================================================ */
function Eyebrow({ children, light = false, align = "left" }) {
  return (
    <p
      style={cinzel}
      className={`text-[10.5px] tracking-[0.28em] uppercase mb-3 flex items-center gap-2.5 ${
        align === "center" ? "justify-center" : "justify-start"
      } ${light ? "text-[#c9a84c]/70" : "text-[#a07828]"}`}
    >
      <span className={`w-6 h-px ${light ? "bg-[#c9a84c]/50" : "bg-[#c9a84c]"}`} />
      {children}
    </p>
  );
}

function SectionHeading({ children, light = false, align = "left" }) {
  return (
    <h2
      style={cinzel}
      className={`font-black text-[1.3rem] sm:text-[1.6rem] md:text-[1.85rem] uppercase tracking-[0.05em] mb-8 ${
        align === "center" ? "text-center" : "text-left"
      } ${light ? "text-white" : "text-[#1a0f02]"}`}
    >
      {children}
    </h2>
  );
}

function GoldDivider() {
  return <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />;
}

function FramedImage({ src, alt, className = "" }) {
  return (
    <div className={`relative rounded-2xl overflow-hidden border border-[#c9a84c]/25 ${className}`}>
      <img src={src} alt={alt} className="w-full h-full object-cover" />
      <span className="absolute inset-0 ring-1 ring-inset ring-[#c9a84c]/15 rounded-2xl pointer-events-none" />
    </div>
  );
}

/* ============================================================
   PAGE COMPONENT
============================================================ */
export default function MaleHairTransplantPune({ url = "https://pune.hairclinique.in/male-hair-transplants.php" }) {
  const pageRef = useRef(null);
  const heroEyeRef = useRef(null);
  const heroHeadRef = useRef(null);
  const heroSubRef = useRef(null);
  const sectionRefs = useRef([]);
  const addSection = (el) => el && !sectionRefs.current.includes(el) && sectionRefs.current.push(el);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const ease = "power3.out";
      gsap.fromTo(heroEyeRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7, ease });
      gsap.fromTo(heroHeadRef.current, { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 0.75, ease, delay: 0.1 });
      gsap.fromTo(heroSubRef.current, { opacity: 0, y: 22 }, { opacity: 1, y: 0, duration: 0.7, ease, delay: 0.18 });

      sectionRefs.current.forEach((el) => {
        if (!el) return;
        gsap.fromTo(
          el,
          { opacity: 0, y: 36 },
          {
            opacity: 1,
            y: 0,
            duration: 0.75,
            ease,
            scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none reverse" },
          }
        );
      });
    }, pageRef);
    return () => ctx.revert();
  }, []);

  /* full-bleed wrapper: fills the viewport with generous edge padding instead of a centered max-width column */
  const wrap = "w-full px-5 sm:px-10 lg:px-16 xl:px-24";
  const sec = "py-16 sm:py-20 md:py-24";

  return (
    <>
<SEO
        title="Male Hair Transplant in Pune | Clinique Internationale"
        description="Advanced male hair transplant treatments in Pune at Clinique Internationale, led by Dr. Ajinkya Patil. Natural, permanent results for men."
        url={url}
      />
      <link
        href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=swap"
        rel="stylesheet"
      />

<div
  ref={pageRef}
  className="relative w-full bg-[#f2ece0] overflow-hidden mt-16 lg:mt-20"
  style={{ fontFamily: "'Raleway', sans-serif" }}
>
  {/* ══════════════════════════════════════
      HERO full width
  ══════════════════════════════════════ */}
  <div className="relative w-full h-[200px] xs:h-[280px] sm:h-[340px] md:h-[700px] flex items-center justify-end overflow-hidden">
    <img className="absolute inset-0 w-full h-full object-cover object-[center_20%] md:object-center" src={HeroImg} alt={PAGE.hero.heading} />
    <div className="absolute inset-0" />
    <div
      className="absolute inset-0 opacity-40 pointer-events-none"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")",
      }}
    />
    <div className="relative z-10 text-right px-4 sm:px-10 md:px-16 max-w-[85%] sm:max-w-xl ml-auto mr-0">
      <p ref={heroEyeRef} style={cinzel} className="text-[8px] xs:text-[9px] sm:text-[10.5px] tracking-[0.2em] xs:tracking-[0.24em] sm:tracking-[0.32em] uppercase text-[#000000] mb-2 sm:mb-4 flex items-center justify-end gap-2 sm:gap-3">
        <span className="w-4 sm:w-7 h-px bg-[#c9a84c]" />
        {PAGE.hero.eyebrow}
        <span className="w-4 sm:w-7 h-px bg-[#c9a84c]" />
      </p>
      <h1
        ref={heroHeadRef}
        style={cinzel}
        className="font-black text-[1.4rem] xs:text-[1.8rem] sm:text-[2.8rem] md:text-[3.4rem] tracking-[0.02em] sm:tracking-[0.04em] uppercase text-black mb-2 sm:mb-3 leading-[1.15] sm:leading-tight"
      >
        {PAGE.hero.heading}
      </h1>
      <p ref={heroSubRef} className="text-[11px] xs:text-[12px] sm:text-[15px] text-black/70 leading-relaxed ml-auto">
        {PAGE.hero.sub}
      </p>
    </div>
  </div>

        {/* ── BREADCRUMB full width with background image ── */}
        <div className="relative w-full overflow-hidden">
          <img className="absolute inset-0 w-full h-full object-cover" src={BreadcrumbImg} alt="" aria-hidden="true" />
          <div className="absolute inset-0 bg-[#0e0905]/88" />
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/25 to-transparent" />
          <div className={`relative ${wrap} py-3.5 flex flex-wrap items-center gap-2`}>
            {PAGE.breadcrumb.map((b, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <span className="text-[#c9a84c]/30 text-xs">›</span>}
                {b.href ? (
                  <a href={b.href} style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]/60 hover:text-[#c9a84c] transition-colors">
                    {b.label}
                  </a>
                ) : (
                  <span style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]">
                    {b.label}
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════
            INTRO
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="max-w-4xl mx-auto text-center">
            <Eyebrow align="center">{PAGE.intro.eyebrow}</Eyebrow>
            <SectionHeading align="center">{PAGE.intro.heading}</SectionHeading>
            {PAGE.intro.paragraphs.map((p, i) => (
              <p key={i} className="text-[14.5px] leading-[1.9] text-[#3a2e1e] mb-4">
                {p}
              </p>
            ))}
            <p className="text-[14.5px] leading-[1.9] text-[#3a2e1e]">
              In this article, we will delve into the world of male{" "}
              <a href={PAGE.intro.link.href} style={cinzel} className="font-bold text-[#a07828] hover:text-[#c9a84c] transition-colors">
                {PAGE.intro.link.label}
              </a>
              {PAGE.intro.afterLink}
            </p>
          </div>
        </div>

        {/* ══════════════════════════════════════
            WHAT IS IT (text + image)
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#faf8f4] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
              <FramedImage src={OverviewImg} alt="Hair transplant consultation" className="h-[260px] sm:h-[320px] md:h-[380px] order-first" />
              <div>
                <Eyebrow>{PAGE.whatIsIt.eyebrow}</Eyebrow>
                <SectionHeading>{PAGE.whatIsIt.heading}</SectionHeading>
                {PAGE.whatIsIt.paragraphs.map((p, i) => (
                  <p key={i} className="text-[14.5px] leading-[1.9] text-[#3a2e1e] mb-4">
                    {p}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            CAUSES OF HAIR LOSS (dark band)
        ══════════════════════════════════════ */}
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection}>
              <Eyebrow light align="center">{PAGE.causes.eyebrow}</Eyebrow>
              <SectionHeading light align="center">{PAGE.causes.heading}</SectionHeading>
              <p className="text-[13.5px] leading-[1.9] text-white/60 max-w-3xl mx-auto text-center mb-10">{PAGE.causes.intro}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
                {PAGE.causes.items.map((c, i) => (
                  <div
                    key={i}
                    className="relative overflow-hidden bg-gradient-to-br from-[#c9a84c]/[0.07] to-[#c9a84c]/[0.02] border border-[#c9a84c]/15 rounded-2xl p-6 hover:border-[#c9a84c]/30 transition-colors"
                  >
                    <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/35 to-transparent" />
                    <span style={cinzel} className="font-black text-[1.6rem] leading-none text-[#c9a84c]/25 select-none block mb-3">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div style={cinzel} className="text-[12px] font-bold tracking-[0.05em] uppercase text-white/90 mb-2">
                      {c.title}
                    </div>
                    <p className="text-[12.5px] leading-[1.75] text-white/55">{c.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            TECHNIQUES (image + list)
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection}>
            <Eyebrow align="center">{PAGE.techniques.eyebrow}</Eyebrow>
            <SectionHeading align="center">{PAGE.techniques.heading}</SectionHeading>
            <p className="text-[14.5px] leading-[1.9] text-[#3a2e1e] max-w-3xl mx-auto text-center mb-10">{PAGE.techniques.intro}</p>

            <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 items-start">
              <div className="flex flex-col gap-5">
                {PAGE.techniques.items.map((t) => (
                  <div key={t.num} className="bg-white border border-[#c9a84c]/18 rounded-2xl p-6 relative overflow-hidden hover:shadow-[0_8px_28px_rgba(90,60,10,0.08)] transition-shadow">
                    <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                    <div className="flex gap-5 items-start">
                      <span style={cinzel} className="font-black text-[2rem] leading-none text-[#c9a84c]/20 select-none flex-shrink-0">
                        {t.num}
                      </span>
                      <div>
                        <div style={cinzel} className="text-[13px] font-bold tracking-[0.05em] uppercase text-[#1a0f02] mb-2">
                          {t.title}
                        </div>
                        <p className="text-[13px] leading-[1.8] text-[#6b5535]">{t.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <FramedImage src={TechniquesImg} alt="Hair transplant surgical instruments" className="h-[260px] sm:h-[340px] lg:h-full lg:sticky lg:top-24" />
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            BENEFITS
        ══════════════════════════════════════ */}
        <GoldDivider />
        <div className="bg-[#faf8f4] py-16 sm:py-20 md:py-24">
          <div className={wrap}>
            <div ref={addSection} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center mb-12">
              <div>
                <Eyebrow>{PAGE.benefits.eyebrow}</Eyebrow>
                <SectionHeading>{PAGE.benefits.heading}</SectionHeading>
                <p className="text-[14.5px] leading-[1.9] text-[#3a2e1e]">{PAGE.benefits.intro}</p>
              </div>
              <FramedImage src={BenefitsImg} alt="Confident patient after hair transplant" className="h-[240px] sm:h-[300px]" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
              {PAGE.benefits.items.map((b, i) => (
                <div
                  key={i}
                  className="bg-white border border-[#c9a84c]/18 rounded-2xl p-6 relative overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_12px_32px_rgba(90,60,10,0.1)] transition-all duration-300"
                >
                  <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#c9a84c]/10 to-[#c9a84c]/[0.03] border border-[#c9a84c]/20 flex items-center justify-center mb-4">
                    <span style={cinzel} className="text-[12px] font-black text-[#c9a84c]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div style={cinzel} className="text-[12.5px] font-bold tracking-[0.06em] uppercase text-[#1a0f02] mb-2">
                    {b.title}
                  </div>
                  <p className="text-[13px] leading-[1.75] text-[#6b5535]">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <GoldDivider />

        {/* ══════════════════════════════════════
            CTA
        ══════════════════════════════════════ */}
        <div className={`${wrap} ${sec}`}>
          <div ref={addSection} className="max-w-xl mx-auto text-center">
            <Eyebrow align="center">{PAGE.cta.eyebrow}</Eyebrow>
            <h3
              style={cinzel}
              className="font-black text-[1.3rem] sm:text-[1.65rem] md:text-[1.9rem] text-[#1a0f02] uppercase tracking-[0.06em] mb-3.5"
            >
              {PAGE.cta.heading}
            </h3>
            <p className="text-[14px] text-[#6b5535] leading-[1.75] mb-8">{PAGE.cta.sub}</p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <a
                href={PAGE.cta.primary.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...cinzel, backgroundImage: "linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)", backgroundSize: "200% 100%" }}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-3.5 rounded-md text-[#1a0f02] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(201,168,76,0.35)] transition-all inline-block"
              >
                {PAGE.cta.primary.label}
              </a>
              <a
                href={PAGE.cta.secondary.href}
                style={cinzel}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-[13px] rounded-md bg-transparent border border-[#1a0f02]/35 text-[#1a0f02] hover:-translate-y-0.5 hover:border-[#c9a84c] hover:text-[#a07828] transition-all inline-block"
              >
                {PAGE.cta.secondary.label}
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}